import { Vue, Component, Prop } from 'vue-property-decorator';

import ValidationService from '../../../services/ValidationService';
import GradeResponse from '../../../models/responses/GradeResponse';
import Validation from '../../../models/Validation';
import ValidationObject from '../../../models/ValidationObject';

/**
 * Wish grade form to calculate the needed grade to have a certain average grade.
 *
 * @param grades - array of all grades in the subject
 */
@Component({
  name: 'WishGrade',
})
export default class WishGrade extends Vue {
  @Prop(Array) grades: Array<GradeResponse> | undefined;

  private wishGradeForm: { [key: string]: number | null } = {
    averageGrade: null as number | null,
    weight: 1,
  };
  private wishGrade: number | null = null;
  private wishGradeValidation: { [key: string]: Validation } = {
    averageGrade: {
      type: 'grade',
      isValid: false,
      message: null,
    } as Validation,
    weight: { type: 'weight', isValid: false, message: null } as Validation,
  };

  get validate(): (
    type: string,
    value: unknown,
    optional?: boolean,
  ) => Validation {
    return ValidationService.validate;
  }

  get validateObject(): (
    validations: ValidationObject,
    object: { [key: string]: unknown },
  ) => ValidationObject {
    return ValidationService.validateObject;
  }

  get isObjectValid(): (form: { [key: string]: Validation }) => boolean {
    return ValidationService.isObjectValid;
  }

  get wishGradeText() {
    if (this.wishGrade !== 0 && !this.wishGrade) return;
    return this.wishGrade >= 1 && this.wishGrade <= 6
      ? this.wishGrade.toFixed(2)
      : this.$t('wishGrade.notPossible');
  }

  /**
   * Runs validation after the component has been mounted.
   */
  async mounted() {
    this.validateObject(this.wishGradeValidation, this.wishGradeForm);

    await setTimeout(() => {
      (this.$refs.gradeInput as HTMLSdxInputElement).doFocus();
    }, 200);
  }

  /**
   * Updates the wish grade form with new properties
   *
   * @remarks
   * Casts the properties to the number format before updating the form
   *
   * @param form - object containing new properties to add to the wish grade form
   */
  private setForm(form: {
    averageGrade: number | null;
    weight: number | null;
  }): void {
    if (!form) return;
    if (form.averageGrade)
      form.averageGrade = Number(form.averageGrade) || null;
    if (form.weight) form.weight = Number(form.weight) || null;
    this.wishGradeForm = { ...this.wishGradeForm, ...form };

    this.validateObject(this.wishGradeValidation, this.wishGradeForm);
  }

  /**
   * Closes grade card via x button.
   */
  private toggleWishGradeForm() {
    this.$store.commit('toggleWishGradeForm');
  }

  /**
   * Calculates the needed grade based on the desired average and weight of the grade.
   *
   * @remarks
   * Formula:
   * x: needed grade in next test
   * a: desired grade average
   * b: weight of next test
   * g1,2...: existing grades
   * w1,2...: existing grade weights
   *
   * x = (a(w1+w2+...+b)-g1w1-g2w2-...) / b
   *
   * @returns the needed grade for the next test
   */
  private calcWishGrade() {
    if (
      !this.wishGradeForm ||
      !this.wishGradeForm.averageGrade ||
      !this.wishGradeForm.weight ||
      !this.grades
    )
      return;

    const a = this.wishGradeForm.averageGrade;
    const b = this.wishGradeForm.weight;
    const grades = this.grades.filter((g) => !g.canceled);
    const weights = b + grades.map((g) => g.weight).reduce((i, y) => i + y, 0);
    const subtractions = grades
      .map((g) => g.grade * g.weight)
      .reduce((i, y) => i - y, 0);

    const x = (a * weights + subtractions) / b;
    this.wishGrade = x;
  }

  /**
   * Checks if the form is valid and if so, calls the calc function.
   */
  private async submitWhenValid() {
    if (!this.isObjectValid(this.wishGradeValidation)) return;
    this.calcWishGrade();
  }
}
